import Phaser from "phaser";
import { SKY_COLORS } from "../art/modernArt";
import { FONT, GAME_HEIGHT, GAME_WIDTH, TILE } from "../config";
import { GROUND_ROW } from "../constants";
import { ENEMY_TEAMMATES, TEAMMATE_VISUALS } from "../data/teammates";
import { createRun, loadHighScore } from "../systems/score";
import { synthAudio } from "../systems/SynthAudio";

export class MenuScene extends Phaser.Scene {
  private started = false;

  constructor() {
    super("MenuScene");
  }

  create(): void {
    this.started = false;
    synthAudio.setTheme("overworld");
    synthAudio.setPaused(false);
    const container = document.getElementById("game-container");
    if (container) {
      container.dataset.state = "menu";
      delete container.dataset.touch;
    }

    // Flat 1985 sky with the same composed scenery the stages use.
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, SKY_COLORS.overworld, 1).setOrigin(0);
    const groundY = GROUND_ROW * TILE;
    this.add.image(120, groundY, "deco-hill-3").setOrigin(0.5, 1);
    this.add.image(560, groundY, "deco-hill-1").setOrigin(0.5, 1);
    this.add.image(90, 208, "deco-cloud-2").setOrigin(0.5, 1);
    this.add.image(470, 232, "deco-cloud-3").setOrigin(0.5, 1);
    for (let tx = 0; tx * TILE < GAME_WIDTH; tx += 1) {
      this.add.image(tx * TILE, groundY, "tile-ground").setOrigin(0, 0);
      this.add.image(tx * TILE, groundY + TILE, "tile-dirt").setOrigin(0, 0);
    }

    // Boxed logo plate in the spirit of the original title card.
    this.add
      .rectangle(GAME_WIDTH / 2, 74, 430, 92, 0xb2473f, 1)
      .setStrokeStyle(4, 0x11152e, 1);
    this.add
      .rectangle(GAME_WIDTH / 2, 74, 418, 80, 0xd9634f, 1)
      .setStrokeStyle(2, 0xffd9a8, 0.7);
    this.add
      .text(GAME_WIDTH / 2 + 3, 62, "SUPER QUINT BROS", {
        fontFamily: FONT,
        fontSize: "42px",
        fontStyle: "bold",
        color: "#11152e",
        stroke: "#11152e",
        strokeThickness: 8,
      })
      .setOrigin(0.5);
    this.add
      .text(GAME_WIDTH / 2, 58, "SUPER QUINT BROS", {
        fontFamily: FONT,
        fontSize: "42px",
        fontStyle: "bold",
        color: "#fff3d0",
        stroke: "#11152e",
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setResolution(2);
    this.add
      .text(GAME_WIDTH / 2, 98, "WORLD 1  ·  THE TEAM STRIKES BACK", {
        fontFamily: FONT,
        fontSize: "13px",
        fontStyle: "bold",
        color: "#ffe9c4",
        stroke: "#11152e",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setResolution(2);

    const best = loadHighScore(window.localStorage);
    this.add
      .text(GAME_WIDTH / 2, 134, `TOP SCORE  ${best.toString().padStart(6, "0")}`, {
        fontFamily: FONT,
        fontSize: "13px",
        fontStyle: "bold",
        color: "#ffd34e",
        stroke: "#11152e",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setResolution(2);

    const start = this.add
      .text(GAME_WIDTH / 2, 169, "START ADVENTURE", {
        fontFamily: FONT,
        fontSize: "18px",
        fontStyle: "bold",
        color: "#11152e",
        backgroundColor: "#62e6d3",
        padding: { x: 24, y: 10 },
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setInteractive({ useHandCursor: true });
    start.on("pointerover", () => {
      start.setBackgroundColor("#fff3d0");
      start.setScale(1.03);
      synthAudio.play("ui");
    });
    start.on("pointerout", () => {
      start.setBackgroundColor("#62e6d3");
      start.setScale(1);
    });
    start.on("pointerdown", () => this.startGame());
    this.tweens.add({
      targets: start,
      alpha: { from: 0.76, to: 1 },
      duration: 680,
      yoyo: true,
      repeat: -1,
    });

    const heroBody = this.add
      .image(106, 322, "player-super-idle")
      .setOrigin(0.5, 1)
      .setScale(1.45);
    const heroHead = this.add
      .image(106, 249, TEAMMATE_VISUALS.part.portraitKey)
      .setDisplaySize(52, 52);
    this.tweens.add({
      targets: [heroBody, heroHead],
      y: "-=4",
      duration: 850,
      yoyo: true,
      repeat: -1,
      ease: "Sine.InOut",
    });

    this.add
      .text(182, 229, "ENEMY ROSTER", {
        fontFamily: FONT,
        fontSize: "12px",
        fontStyle: "bold",
        color: "#fff3d0",
        stroke: "#11152e",
        strokeThickness: 4,
      })
      .setResolution(2);
    ENEMY_TEAMMATES.forEach((id, index) => {
      const portrait = this.add
        .image(220 + index * 62, 274, TEAMMATE_VISUALS[id].portraitKey)
        .setDisplaySize(36, 36);
      this.tweens.add({
        targets: portrait,
        y: portrait.y + (index % 2 === 0 ? -5 : 5),
        duration: 750 + index * 70,
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      });
    });

    this.add
      .text(
        183,
        309,
        "MOVE  ARROWS/WASD     JUMP  SPACE/Z     RUN  SHIFT/X     FIRE  F/J",
        {
          fontFamily: FONT,
          fontSize: "9px",
          fontStyle: "bold",
          color: "#ffffff",
          stroke: "#11152e",
          strokeThickness: 3,
        },
      )
      .setResolution(2);
    this.add
      .text(183, 331, "P / ESC  PAUSE       M  SOUND       TOUCH CONTROLS SUPPORTED", {
        fontFamily: FONT,
        fontSize: "9px",
        fontStyle: "bold",
        color: "#d8f8f0",
        stroke: "#11152e",
        strokeThickness: 3,
      })
      .setResolution(2);

    const sound = this.add
      .text(
        GAME_WIDTH - 16,
        10,
        synthAudio.isEnabled ? "SOUND ON" : "SOUND OFF",
        {
          fontFamily: FONT,
          fontSize: "10px",
          fontStyle: "bold",
          color: "#ffffff",
          backgroundColor: "#11152dcc",
          padding: { x: 9, y: 6 },
        },
      )
      .setOrigin(1, 0)
      .setResolution(2)
      .setInteractive({ useHandCursor: true });
    sound.on("pointerdown", () => {
      const enabled = synthAudio.toggle();
      sound.setText(enabled ? "SOUND ON" : "SOUND OFF");
    });

    this.input.once("pointerdown", () => void synthAudio.unlock());
    this.input.keyboard?.once("keydown-ENTER", () => this.startGame());
    this.input.keyboard?.once("keydown-SPACE", () => this.startGame());
  }

  private startGame(): void {
    if (this.started) return;
    this.started = true;
    void synthAudio.unlock();
    synthAudio.play("ui");
    this.registry.set("run", createRun("1-1"));
    this.cameras.main.fadeOut(350, 17, 21, 46);
    this.time.delayedCall(330, () => this.scene.start("LevelScene"));
  }
}
